import { useQuery } from '@tanstack/react-query'
import { tasksApi } from '@/api'
import { useTimeLogSummary } from './useTimeLogs'
import { useUpcomingDeadlines } from './useStatutory'

/**
 * Query keys for dashboard stats
 */
export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: () => [...dashboardKeys.all, 'stats'] as const,
}

/**
 * Dashboard stats interface
 */
export interface DashboardStats {
  tasksDueToday: number
  hoursLoggedToday: number
  overdueTasks: number
  upcomingDeadlines: number
  tasksInProgress: number
  tasksCompleted: number
}

/**
 * Hook to fetch aggregated dashboard statistics
 */
export function useDashboardStats() {
  const today = new Date().toISOString().split('T')[0]

  // Fetch tasks due today
  const tasksDueTodayQuery = useQuery({
    queryKey: [...dashboardKeys.stats(), 'dueToday'],
    queryFn: () => tasksApi.list({
      due_before: today,
      due_after: today,
      per_page: 100,
    }),
  })

  // Fetch overdue tasks
  const overdueQuery = useQuery({
    queryKey: [...dashboardKeys.stats(), 'overdue'],
    queryFn: () => {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      return tasksApi.list({
        due_before: yesterday.toISOString().split('T')[0],
        per_page: 100,
      })
    },
  })

  // Fetch in-progress tasks
  const inProgressQuery = useQuery({
    queryKey: [...dashboardKeys.stats(), 'inProgress'],
    queryFn: () => tasksApi.list({
      status: 'in_progress',
      per_page: 100,
    }),
  })

  // Today's time logged
  const timeQuery = useTimeLogSummary({
    from_date: today,
    to_date: today,
  })

  // Upcoming deadlines (7 days)
  const deadlinesQuery = useUpcomingDeadlines(7)

  const isLoading =
    tasksDueTodayQuery.isLoading ||
    overdueQuery.isLoading ||
    inProgressQuery.isLoading ||
    timeQuery.isLoading ||
    deadlinesQuery.isLoading

  const isError =
    tasksDueTodayQuery.isError ||
    overdueQuery.isError ||
    inProgressQuery.isError ||
    timeQuery.isError ||
    deadlinesQuery.isError

  // Filter overdue tasks (exclude completed)
  const overdueTasks = overdueQuery.data?.items?.filter(
    task => task.status !== 'completed'
  ) || []

  const stats: DashboardStats = {
    tasksDueToday: tasksDueTodayQuery.data?.total ?? 0,
    hoursLoggedToday: timeQuery.data?.total_hours ?? 0,
    overdueTasks: overdueTasks.length,
    upcomingDeadlines: deadlinesQuery.data?.length ?? 0,
    tasksInProgress: inProgressQuery.data?.total ?? 0,
    tasksCompleted: 0, // Could add separate query if needed
  }

  return {
    stats,
    isLoading,
    isError,
    tasksDueToday: tasksDueTodayQuery.data?.items ?? [],
    overdueTasks,
    inProgressTasks: inProgressQuery.data?.items ?? [],
    upcomingDeadlines: deadlinesQuery.data ?? [],
  }
}